import type {
  NotificationPrefId,
  NotificationPreferences,
  SettingsRecord,
  SoundPreferences,
} from '../models/settings.model'
import { settingsRepo } from './settings.repo'

/**
 * Settings service — read/write accessors plus a small bundle of *decision*
 * helpers (`shouldNotify`, `shouldPlaySound`) so consumers don't have to
 * re-implement DND / mute-all logic locally.
 */
export const settingsService = {
  get(): SettingsRecord {
    return settingsRepo.load()
  },

  setOption(id: NotificationPrefId, value: boolean): SettingsRecord {
    const current = settingsRepo.load()
    const next: SettingsRecord = {
      ...current,
      notifications: { ...current.notifications, [id]: value },
    }
    settingsRepo.save(next)
    return next
  },

  setSounds(patch: Partial<SoundPreferences>): SettingsRecord {
    const current = settingsRepo.load()
    const next: SettingsRecord = {
      ...current,
      sounds: { ...current.sounds, ...patch },
    }
    settingsRepo.save(next)
    return next
  },

  reset(): SettingsRecord {
    settingsRepo.clear()
    return settingsRepo.load()
  },
}

/**
 * Decision helpers — pure functions that take the active prefs and answer a
 * specific yes/no question. Centralising them here means the floating chat
 * widget, the reminder scheduler and any future consumer all agree on the
 * priority order: DND beats per-channel toggles; per-channel toggles beat
 * sound toggles; mute-all beats every sound flag.
 */
export type NotifyChannel = 'chat' | 'reminder' | 'call' | 'participant'

export function shouldShowNotification(
  prefs: NotificationPreferences,
  channel: NotifyChannel,
): boolean {
  if (prefs['do-not-disturb']) return false
  switch (channel) {
    case 'chat':
      return prefs['enable-chat-notifications']
    case 'reminder':
      // Two toggles gate reminders: the master `meeting-reminders` switch
      // and the legacy "turn off completely" override.
      return prefs['meeting-reminders'] && !prefs['turn-off-reminders']
    case 'call':
      // Calls always surface visually — only the sound is gated separately.
      return true
    case 'participant':
      // Aggregated; the join/leave flags decide which alert kind actually
      // fires — see `shouldShowParticipantAlert`.
      return prefs['participant-join-alert'] || prefs['participant-leave-alert']
    default:
      return true
  }
}

export function shouldShowParticipantAlert(
  prefs: NotificationPreferences,
  kind: 'join' | 'leave',
): boolean {
  if (prefs['do-not-disturb']) return false
  return kind === 'join'
    ? prefs['participant-join-alert']
    : prefs['participant-leave-alert']
}

export function shouldPlaySound(
  prefs: NotificationPreferences,
  channel: NotifyChannel,
): boolean {
  if (prefs['do-not-disturb']) return false
  if (prefs['mute-all-sounds']) return false
  switch (channel) {
    case 'chat':
      return prefs['enable-chat-notifications'] && prefs['play-sound-chat']
    case 'call':
      return prefs['incoming-calls-sound']
    case 'reminder':
      return (
        prefs['meeting-reminders'] &&
        !prefs['turn-off-reminders'] &&
        prefs['meeting-reminder-sound']
      )
    case 'participant':
      return prefs['participant-sound']
    default:
      return false
  }
}
